import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Radio, MapPin, Square, Clock, Send, CheckCircle2, Trash2 } from "lucide-react";
import api from "../../lib/api";

/* Traveler side: start/stop Guardian sessions, share location, one-tap statuses. */

const MODES = [
  ["check_in", "Check-In Only", "No live location — contacts get your check-ins and statuses"],
  ["journey", "Journey Sharing", "Share one journey (taxi, drive, transfer) — ends on arrival or timer"],
  ["trip", "Trip Guardian", "Ongoing updates for your trip dates, at the precision you pick"],
];
const PRECISIONS = [
  ["exact", "Exact GPS"], ["approx", "Approximate (~1 km)"], ["city", "City-level"], ["status", "Status only"],
];
const DELAYS = [[0, "No delay"], [15, "15 min"], [30, "30 min"], [60, "1 hour"], [120, "2 hours"], [1440, "24 hours"]];
const STATUSES = [
  ["im_safe", "I'm safe", "bg-emerald-600"], ["arrived", "Arrived", "bg-blue-600"],
  ["boarding", "Boarding now", "bg-indigo-600"], ["delayed", "Delayed", "bg-amber-600"],
  ["at_hotel", "At the hotel", "bg-teal-600"], ["plans_changed", "Plans changed", "bg-zinc-600"],
  ["call_me", "Call me", "bg-orange-600"], ["need_help", "Need help", "bg-rose-600"],
];

export default function GuardianShare() {
  const [sessions, setSessions] = useState(null);
  const [circles, setCircles] = useState([]);
  const [wizard, setWizard] = useState(false);
  const [form, setForm] = useState({ circle_id: "", mode: "trip", precision: "approx", delay_minutes: 0, duration_hours: 24, journey_label: "", destination: "" });
  const [busy, setBusy] = useState(false);

  const load = () => {
    api.get("/guardian/sessions/mine").then((r) => setSessions(r.data.items)).catch(() => setSessions([]));
    api.get("/guardian/circles").then((r) => setCircles(r.data.items)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const active = (sessions || []).filter((s) => s.status === "active");

  const start = async () => {
    if (!form.circle_id) { toast.error("Pick a circle — the people who may see you"); return; }
    setBusy(true);
    try {
      await api.post("/guardian/sessions", { ...form, delay_minutes: Number(form.delay_minutes), duration_hours: Number(form.duration_hours) });
      toast.success("Guardian session started");
      setWizard(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not start session"); }
    setBusy(false);
  };

  const stop = async (sid) => {
    try { await api.post(`/guardian/sessions/${sid}/stop`); toast.success("Sharing stopped immediately"); load(); }
    catch { toast.error("Could not stop"); }
  };

  const shareLocation = (sid) => {
    if (!navigator.geolocation) { toast.error("This browser has no location support"); return; }
    toast.info("Getting your location…");
    navigator.geolocation.getCurrentPosition(async (pos) => {
      try {
        const r = await api.post(`/guardian/sessions/${sid}/location`, {
          lat: pos.coords.latitude, lon: pos.coords.longitude, accuracy: pos.coords.accuracy, source: "browser",
        });
        toast.success(r.data.visible_from > r.data.recorded_at ? "Location saved — will appear to viewers after your delay" : "Location shared");
      } catch { toast.error("Could not share location"); }
    }, () => toast.error("Location permission denied"), { enableHighAccuracy: true, timeout: 15000 });
  };

  const sendStatus = async (sid, type) => {
    try { await api.post(`/guardian/sessions/${sid}/status`, { status_type: type }); toast.success("Status sent to your circle"); }
    catch { toast.error("Could not send status"); }
  };

  const deleteHistory = async (sid) => {
    try { const r = await api.delete(`/guardian/sessions/${sid}/history`); toast.success(`Deleted ${r.data.deleted} location points`); load(); }
    catch { toast.error("Could not delete"); }
  };

  if (!sessions) return <p className="text-sm text-zinc-400">Loading…</p>;

  return (
    <div data-testid="guardian-share">
      {active.length === 0 && !wizard && (
        <div className="border border-dashed border-emerald-300 bg-emerald-50/40 rounded-2xl px-5 py-8 text-center" data-testid="guardian-share-empty">
          <Radio className="w-8 h-8 text-emerald-500 mx-auto" />
          <p className="mt-2 text-sm font-700">You're not sharing anything right now.</p>
          <p className="text-xs text-zinc-500 mt-1">Start a Guardian session when you want trusted people to follow your journey.</p>
        </div>
      )}

      {active.map((s) => (
        <div key={s.id} className="border border-emerald-300 bg-white rounded-2xl p-4 mb-4 shadow-sm" data-testid={`guardian-session-${s.id}`}>
          <div className="flex flex-wrap items-center gap-2">
            <span className="inline-flex items-center gap-1.5 bg-emerald-100 text-emerald-800 rounded-full px-2.5 py-1 text-[11px] font-800 uppercase animate-pulse"><Radio className="w-3 h-3" /> Active</span>
            <span className="text-sm font-800">{MODES.find(([m]) => m === s.mode)?.[1]}</span>
            {s.journey_label && <span className="text-xs text-zinc-500">· {s.journey_label}</span>}
            <span className="ml-auto text-[11px] text-zinc-400 inline-flex items-center gap-1"><Clock className="w-3 h-3" /> ends {new Date(s.expires_at).toLocaleString()}</span>
          </div>
          <div className="mt-1.5 text-xs text-zinc-500">
            Precision: <b>{PRECISIONS.find(([p]) => p === s.precision)?.[1]}</b> · Delay: <b>{DELAYS.find(([d]) => d === s.delay_minutes)?.[1]}</b> · {s.point_count} location point{s.point_count !== 1 && "s"}
          </div>
          <div className="mt-3 flex flex-wrap gap-2">
            {s.precision !== "status" && (
              <button onClick={() => shareLocation(s.id)} data-testid="guardian-share-location-btn"
                className="inline-flex items-center gap-1.5 bg-blue-700 text-white text-xs font-800 rounded-full px-4 py-2 hover:bg-blue-800"><MapPin className="w-3.5 h-3.5" /> Share my location now</button>
            )}
            <button onClick={() => stop(s.id)} data-testid="guardian-stop-btn"
              className="inline-flex items-center gap-1.5 bg-rose-600 text-white text-xs font-800 rounded-full px-4 py-2 hover:bg-rose-700"><Square className="w-3.5 h-3.5" /> STOP SHARING NOW</button>
            <button onClick={() => deleteHistory(s.id)} data-testid="guardian-delete-history-btn"
              className="inline-flex items-center gap-1.5 border border-zinc-300 text-zinc-600 text-xs font-700 rounded-full px-3 py-2 hover:border-rose-400 hover:text-rose-600"><Trash2 className="w-3.5 h-3.5" /> Delete location history</button>
          </div>
          <div className="mt-4">
            <div className="text-[11px] font-800 uppercase tracking-wide text-zinc-400 mb-1.5">One-tap status to your circle</div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid="guardian-status-buttons">
              {STATUSES.map(([type, label, color]) => (
                <button key={type} onClick={() => sendStatus(s.id, type)} data-testid={`guardian-status-${type}`}
                  className={`${color} text-white text-xs font-800 rounded-xl px-3 py-2.5 hover:opacity-90 inline-flex items-center justify-center gap-1.5`}>
                  <CheckCircle2 className="w-3.5 h-3.5" /> {label}
                </button>
              ))}
            </div>
          </div>
        </div>
      ))}

      {!wizard ? (
        <button onClick={() => setWizard(true)} data-testid="guardian-start-btn"
          className="mt-2 inline-flex items-center gap-2 bg-emerald-600 text-white text-sm font-800 rounded-full px-6 py-3 hover:bg-emerald-700">
          <Radio className="w-4 h-4" /> Start Travel Guardian
        </button>
      ) : (        <div className="mt-2 border border-zinc-200 rounded-2xl p-5 bg-white shadow-sm" data-testid="guardian-wizard">
          <h3 className="font-heading font-800 text-base md:text-lg">Start a Guardian session</h3>
          <div className="mt-4 space-y-4">
            <div>
              <label className="text-xs font-800 uppercase text-zinc-500">Who may see you (circle)</label>
              <select value={form.circle_id} onChange={(e) => setForm({ ...form, circle_id: e.target.value })} data-testid="guardian-wizard-circle"
                className="mt-1 w-full border border-zinc-300 rounded-xl px-3 py-2.5 text-sm bg-white">
                <option value="">Select a circle…</option>
                {circles.map((c) => <option key={c.id} value={c.id}>{c.name} ({c.members?.filter((m) => m.status === "accepted").length || 0} accepted)</option>)}
              </select>
              {circles.length === 0 && <p className="mt-1 text-[11px] text-amber-700">No circles yet — create one in the Circles tab first.</p>}
            </div>
            <div>
              <label className="text-xs font-800 uppercase text-zinc-500">Sharing mode</label>
              <div className="mt-1 grid sm:grid-cols-3 gap-2">
                {MODES.map(([id, label, desc]) => (
                  <button key={id} onClick={() => setForm({ ...form, mode: id })} data-testid={`guardian-wizard-mode-${id}`}
                    className={`text-left border rounded-xl px-3 py-2.5 ${form.mode === id ? "border-emerald-500 bg-emerald-50" : "border-zinc-200 hover:border-zinc-300"}`}>
                    <div className="text-sm font-800">{label}</div>
                    <div className="text-[11px] text-zinc-500 mt-0.5">{desc}</div>
                  </button>
                ))}
              </div>
            </div>
            <div className="grid sm:grid-cols-3 gap-3">
              <div>
                <label className="text-xs font-800 uppercase text-zinc-500">Location precision</label>
                <select value={form.precision} onChange={(e) => setForm({ ...form, precision: e.target.value })} data-testid="guardian-wizard-precision"
                  className="mt-1 w-full border border-zinc-300 rounded-xl px-3 py-2.5 text-sm bg-white">
                  {PRECISIONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs font-800 uppercase text-zinc-500">Delay before viewers see it</label>
                <select value={form.delay_minutes} onChange={(e) => setForm({ ...form, delay_minutes: e.target.value })} data-testid="guardian-wizard-delay"
                  className="mt-1 w-full border border-zinc-300 rounded-xl px-3 py-2.5 text-sm bg-white">
                  {DELAYS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs font-800 uppercase text-zinc-500">Ends after</label>
                <select value={form.duration_hours} onChange={(e) => setForm({ ...form, duration_hours: e.target.value })} data-testid="guardian-wizard-duration"
                  className="mt-1 w-full border border-zinc-300 rounded-xl px-3 py-2.5 text-sm bg-white">
                  {[[1, "1 hour"], [4, "4 hours"], [12, "12 hours"], [24, "1 day"], [72, "3 days"], [168, "1 week"], [336, "2 weeks"]].map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
              </div>
            </div>
            {form.mode === "journey" && (
              <div className="grid sm:grid-cols-2 gap-3">
                <input value={form.journey_label} onChange={(e) => setForm({ ...form, journey_label: e.target.value })} data-testid="guardian-wizard-journey"
                  placeholder="Journey (e.g. Airport → Hotel taxi)" className="border border-zinc-300 rounded-xl px-3 py-2.5 text-sm" />
                <input value={form.destination} onChange={(e) => setForm({ ...form, destination: e.target.value })} data-testid="guardian-wizard-destination"
                  placeholder="Destination" className="border border-zinc-300 rounded-xl px-3 py-2.5 text-sm" />
              </div>
            )}
            <div className="flex gap-2">
              <button onClick={start} disabled={busy} data-testid="guardian-wizard-start"
                className="inline-flex items-center gap-1.5 bg-emerald-600 text-white text-sm font-800 rounded-full px-6 py-2.5 hover:bg-emerald-700 disabled:opacity-50"><Send className="w-4 h-4" /> Start sharing</button>
              <button onClick={() => setWizard(false)} data-testid="guardian-wizard-cancel" className="text-sm font-700 text-zinc-500 px-4 hover:text-zinc-800">Cancel</button>
            </div>
            <p className="text-[11px] text-zinc-400">Sharing ends automatically at the time you chose. You can stop it instantly at any moment — viewers are never notified of revocations.</p>
          </div>
        </div>
      )}

      {/* Past sessions → trip stories */}
      <PastSessions sessions={(sessions || []).filter((s) => s.status !== "active" && s.point_count > 0)} />
    </div>
  );
}

function PastSessions({ sessions }) {
  const [busyId, setBusyId] = useState(null);
  const [links, setLinks] = useState({});
  if (sessions.length === 0) return null;
  const makeStory = async (sid) => {
    setBusyId(sid);
    try {
      const r = await api.post(`/guardian/sessions/${sid}/story`, { title: "My journey", precision: "approx" });
      setLinks((l) => ({ ...l, [sid]: r.data.share_path }));
      toast.success(`Trip story created — ${r.data.points} points, ${r.data.distance_km} km`);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not create story"); }
    setBusyId(null);
  };
  const copy = (path) => {
    navigator.clipboard?.writeText(`${window.location.origin}${path}`);
    toast.success("Share link copied");
  };
  return (
    <div className="mt-8" data-testid="guardian-past-sessions">
      <div className="text-[11px] font-800 uppercase tracking-wide text-zinc-400 mb-2">Finished sessions — turn into a shareable trip story</div>
      <div className="space-y-2">
        {sessions.slice(0, 5).map((s) => (
          <div key={s.id} className="border border-zinc-200 bg-white rounded-xl px-4 py-2.5 flex flex-wrap items-center gap-2 text-xs" data-testid="guardian-past-session-row">
            <span className="font-700 capitalize">{s.mode.replace("_", " ")}</span>
            {s.journey_label && <span className="text-zinc-500">· {s.journey_label}</span>}
            <span className="text-zinc-400">{new Date(s.starts_at).toLocaleDateString()} · {s.point_count} points</span>
            {links[s.id] ? (
              <button onClick={() => copy(links[s.id])} data-testid="guardian-story-copy" className="ml-auto text-blue-700 font-800 hover:underline">Copy share link</button>
            ) : (
              <button onClick={() => makeStory(s.id)} disabled={busyId === s.id} data-testid={`guardian-story-create-${s.id}`}
                className="ml-auto border border-violet-300 text-violet-700 font-800 rounded-full px-3 py-1.5 hover:bg-violet-50 disabled:opacity-50">
                {busyId === s.id ? "Creating…" : "Create trip story"}
              </button>
            )}
          </div>
        ))}
      </div>
      <p className="mt-1.5 text-[10px] text-zinc-400">Stories use approximate (~1 km) locations by default — never your exact positions, hotel or home.</p>
    </div>
  );
}
